/**
 * ZiekteverzuimWidget — Team sick leave metrics overview
 */

import { useEffect, useMemo } from 'react';
import { useWorkforceStore } from '../../stores';
import type { Staff, LeaveEntry } from '../../stores/workforce-store';
import { filterSickLeave } from '../../lib/leave-types';

interface StaffVerzuim {
  name: string;
  percentage: number;
  sickDays: number;
  incidents: number;
  isCurrentlySick: boolean;
}

function calculateVerzuim(staffList: Staff[], leaveEntries: LeaveEntry[]): {
  average: number;
  currentlySick: Staff[];
  frequentVerzuim: StaffVerzuim[];
  top3: StaffVerzuim[];
} {
  const currentYear = new Date().getFullYear();
  const today = new Date().toISOString().split('T')[0]!;
  const yearStart = `${currentYear}-01-01`;

  const staffVerzuim: StaffVerzuim[] = [];
  const currentlySick: Staff[] = [];

  for (const staff of staffList) {
    if (!staff.is_active || staff.contract_type === 'zzp' || staff.contract_type === 'dga') continue;

    const sickEntries = filterSickLeave(leaveEntries)
      .filter(e => e.staff_id === staff.id && e.date >= yearStart)
      .sort((a, b) => a.date.localeCompare(b.date));

    const sickDays = sickEntries.length;
    const sickHours = sickEntries.reduce((sum, e) => sum + (e.hours || 8), 0);

    // Calculate available hours (rough: working days so far * 8)
    const daysSinceYearStart = Math.floor(
      (new Date().getTime() - new Date(yearStart + 'T00:00:00').getTime()) / (1000 * 60 * 60 * 24)
    );
    const workingDays = Math.round(daysSinceYearStart * 5 / 7);
    const hoursPerDay = staff.uren_per_week ? staff.uren_per_week / 5 : 8;
    const availableHours = workingDays * hoursPerDay;
    const percentage = availableHours > 0 ? (sickHours / availableHours) * 100 : 0;

    // Count incidents (contiguous periods)
    let incidents = 0;
    let lastDate = '';
    for (const entry of sickEntries) {
      if (!lastDate || daysBetween(lastDate, entry.date) > 3) {
        incidents++;
      }
      lastDate = entry.date;
    }

    // Currently sick?
    const isSick = sickEntries.some(e => e.date === today || daysBetween(e.date, today) <= 1);
    if (isSick) currentlySick.push(staff);

    staffVerzuim.push({
      name: staff.name,
      percentage: Math.round(percentage * 10) / 10,
      sickDays,
      incidents,
      isCurrentlySick: isSick,
    });
  }

  const totalPct = staffVerzuim.reduce((sum, s) => sum + s.percentage, 0);
  const average = staffVerzuim.length > 0 ? Math.round((totalPct / staffVerzuim.length) * 10) / 10 : 0;

  const frequentVerzuim = staffVerzuim.filter(s => s.incidents > 3);
  const top3 = [...staffVerzuim].sort((a, b) => b.percentage - a.percentage).slice(0, 3);

  return { average, currentlySick, frequentVerzuim, top3 };
}

function daysBetween(a: string, b: string): number {
  return Math.abs(Math.round(
    (new Date(b + 'T00:00:00').getTime() - new Date(a + 'T00:00:00').getTime()) / (1000 * 60 * 60 * 24)
  ));
}

interface ZiekteverzuimWidgetProps {
  staffList: Staff[];
}

export function ZiekteverzuimWidget({ staffList }: ZiekteverzuimWidgetProps) {
  const { leaveEntries, fetchLeave } = useWorkforceStore();

  useEffect(() => {
    const year = new Date().getFullYear();
    fetchLeave({ from: `${year}-01-01`, to: `${year}-12-31` });
  }, [fetchLeave]);

  const verzuim = useMemo(
    () => calculateVerzuim(staffList, leaveEntries),
    [staffList, leaveEntries]
  );

  // Don't render if no sick leave data
  if (leaveEntries.length === 0 && verzuim.currentlySick.length === 0) return null;

  return (
    <div className="mb-4 p-4 bg-white border border-slate-200 rounded-xl">
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
        Team Verzuim ({new Date().getFullYear()})
      </h3>

      <div className="grid grid-cols-3 gap-4 mb-3">
        <div>
          <p className="text-xl font-bold text-main">{verzuim.average}%</p>
          <p className="text-xs text-muted">Gem. verzuim</p>
        </div>
        <div>
          <p className="text-xl font-bold text-main">{verzuim.currentlySick.length}</p>
          <p className="text-xs text-muted">
            Nu ziek{verzuim.currentlySick.length > 0 && (
              <span className="text-slate-400"> ({verzuim.currentlySick.map(s => s.name.split(' ')[0]).join(', ')})</span>
            )}
          </p>
        </div>
        <div>
          <p className="text-xl font-bold text-main">{verzuim.frequentVerzuim.length}</p>
          <p className="text-xs text-muted">Frequent ({'>'}3x)</p>
        </div>
      </div>

      {verzuim.top3.length > 0 && verzuim.top3[0]!.sickDays > 0 && (
        <div className="border-t border-slate-100 pt-2">
          <p className="text-xs text-muted mb-1">Hoogste verzuim:</p>
          {verzuim.top3.filter(s => s.sickDays > 0).map(s => (
            <div key={s.name} className="flex justify-between text-xs py-0.5">
              <span className="text-main">{s.name}</span>
              <span className="text-muted">
                {s.percentage}% ({s.sickDays} dgn)
                {s.incidents > 3 && <span className="text-amber-600 ml-1" title="Frequent verzuim">!</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
